export const QUOTE_GROUP_SYMBOLS = {
  tech: [
    'AAPL',
    'MSFT',
    'NVDA',
    'AMD',
    'AVGO',
    'GOOGL',
    'META',
    'AMZN',
    'CRM',
    'SNOW',
    'DDOG',
    'NET',
    'PLTR',
    'ORCL',
    'ADBE',
    'INTC',
    'QCOM',
    'CSCO',
    'MU',
    'SNDK',
    'IBM',
    'WDC',
    'UBER',
    'ANET',
    'CRWD'
  ],
  etfs: [
    'SPY',
    'QQQ',
    'IWM',
    'DIA',
    'VOO',
    'XLK',
    'XLF',
    'XLE',
    'XLV',
    'XLY',
    'XLP',
    'TLT',
    'HYG',
    'GLD',
    'ARKK'
  ],
  value: [
    'JPM',
    'BAC',
    'MS',
    'GS',
    'C',
    'XOM',
    'CVX',
    'KO',
    'PEP',
    'PG',
    'MCD',
    'WMT',
    'TGT',
    'COST',
    'HD',
    'LOW',
    'JNJ',
    'PFE',
    'MRK',
    'ABT',
    'ABBV'
  ]
} as const;

export type QuoteGroupId = keyof typeof QUOTE_GROUP_SYMBOLS;

export interface HeatMapSectorDefinition {
  id: string;
  name: string;
  symbols: readonly string[];
}

export const HEAT_MAP_SECTORS = [
  {
    id: 'technology',
    name: 'Technology',
    symbols: [
      'AAPL',
      'MSFT',
      'NVDA',
      'AMD',
      'AVGO',
      'GOOGL',
      'META',
      'CRM',
      'ADBE',
      'ORCL',
      'INTC',
      'QCOM',
      'CSCO',
      'MU',
      'ANET',
      'CRWD'
    ]
  },
  {
    id: 'consumer',
    name: 'Consumer',
    symbols: [
      'AMZN',
      'TSLA',
      'NFLX',
      'DIS',
      'NKE',
      'SBUX',
      'MCD',
      'COST',
      'WMT',
      'TGT',
      'HD',
      'LOW'
    ]
  },
  {
    id: 'financials',
    name: 'Financials',
    symbols: [
      'JPM',
      'BAC',
      'MS',
      'GS',
      'C',
      'V',
      'MA',
      'PYPL',
      'COIN'
    ]
  },
  {
    id: 'healthcare',
    name: 'Healthcare',
    symbols: [
      'UNH',
      'LLY',
      'JNJ',
      'PFE',
      'MRK',
      'ABBV',
      'TMO',
      'ABT',
      'AMGN',
      'GILD',
      'REGN'
    ]
  },
  {
    id: 'industrials',
    name: 'Industrials',
    symbols: [
      'BA',
      'CAT',
      'DE',
      'GE',
      'LMT',
      'RTX',
      'F',
      'GM'
    ]
  },
  {
    id: 'energy',
    name: 'Energy / Materials',
    symbols: [
      'XOM',
      'CVX',
      'COP',
      'SLB',
      'GLD'
    ]
  },
  {
    id: 'etfs',
    name: 'ETFs',
    symbols: [
      'SPY',
      'QQQ',
      'IWM',
      'DIA',
      'VOO',
      'XLK',
      'XLF',
      'XLE',
      'XLV',
      'TLT',
      'HYG',
      'GLD',
      'ARKK'
    ]
  }
] as const satisfies readonly HeatMapSectorDefinition[];

const ADDITIONAL_MARKET_SYMBOLS = [
  'NFLX',
  'NOW',
  'SHOP',
  'PANW',
  'WFC',
  'V',
  'MA',
  'AXP',
  'PYPL',
  'COP',
  'SLB',
  'BP',
  'SHEL',
  'TTE',
  'OXY',
  'EOG',
  'MPC',
  'SLV',
  'USO',
  'UNG',
  'EURUSD',
  'GBPUSD',
  'USDJPY',
  'USDCAD',
  'AUDUSD',
  'USDCHF',
  'BTCUSD',
  'ETHUSD',
  'SOLUSD',
  'XRPUSD',
  'BABA',
  'TSM',
  'ASML',
  'SAP',
  'SONY',
  'TM',
  'NVO',
  'AZN',
  'HSBC',
  'RY',
  'COIN',
  'MSTR',
  'RIVN',
  'F',
  'GM',
  'BA',
  'CAT',
  'DE',
  'GE',
  'LMT',
  'RTX',
  'NKE',
  'SBUX',
  'DIS',
  'CMCSA',
  'UNH',
  'LLY',
  'TMO',
  'AMGN',
  'GILD',
  'REGN'
] as const;

export const DEFAULT_MARKET_SYMBOLS: string[] = uniqueSymbols([
  ...QUOTE_GROUP_SYMBOLS.tech,
  ...QUOTE_GROUP_SYMBOLS.value,
  ...QUOTE_GROUP_SYMBOLS.etfs,
  ...HEAT_MAP_SECTORS.flatMap((sector) => sector.symbols),
  ...ADDITIONAL_MARKET_SYMBOLS
]);

function uniqueSymbols(symbols: readonly string[]): string[] {
  return Array.from(
    new Set(
      symbols
        .map(normalizeSymbol)
        .filter(Boolean)
    )
  );
}

function normalizeSymbol(symbol: string): string {
  return symbol.trim().toUpperCase();
}